import { useTranslation } from 'react-i18next';

import { useFeature, useViewerRoles, useWorkspace } from '@treams/treams-hooks';
import { FeatureName } from '@treams/treams-network';
import {
	ArrowLeftIcon,
	BellIcon,
	Button,
	CashIcon,
	CityIcon,
	ConversationIcon,
	DashboardIcon,
	DialogueIcon,
	FriendsIcon,
	GearIcon,
	GridIcon,
	HammerIcon,
	HeartRateIcon,
	HistoryIcon,
	LibraryIcon,
	LockIcon,
	Sidebar,
	SuitcaseIcon,
	SyncIcon,
	TargetIcon,
} from '@treams/treams-ui';

import { useManagerMode } from '../ManagerModeContext';

export const DefaultSettingsItems = () => {
	const { t } = useTranslation();

	const workspace = useWorkspace();
	const { isHRBusinessPartner } = useViewerRoles();
	const hasCycles = useFeature(FeatureName.Cycles);
	const hasGoalTemplates = useFeature(FeatureName.JobGoalTemplates);
	const hasFeedback = useFeature(FeatureName.Feedback);
	const hasConversations = useFeature(FeatureName.Evaluations);
	const hasLearning = useFeature(FeatureName.Learning);
	const hasTalentReviews = useFeature(FeatureName.TalentReviews);
	const hasEngagement = useFeature(FeatureName.Engagement);
	const hasBilling = useFeature(FeatureName.Billing);

	const managermode = useManagerMode();

	return (
		<>
			{managermode.enabled ? (
				<Button
					title={t('sidebar.manager.back')}
					type="secondary"
					icon={<ArrowLeftIcon />}
					link="/manager"
					mb={4}
				/>
			) : (
				<Button
					title={t('sidebar.user.back')}
					type="secondary"
					icon={<ArrowLeftIcon />}
					link="/"
					mb={4}
				/>
			)}
			<Sidebar.Item
				small
				exact
				icon={<DashboardIcon />}
				title={t('settings.overview.title')}
				path="/settings"
			/>
			<Sidebar.SectionTitle mt={4} mb={1}>
				{t('settings.section.general')}
			</Sidebar.SectionTitle>
			<Sidebar.Item
				small
				icon={<CityIcon />}
				title={t('settings.organization.title')}
				path="/settings/organization"
			/>
			<Sidebar.Item
				small
				icon={<FriendsIcon />}
				title={t('settings.employees.title')}
				path="/settings/employees"
			/>
			<Sidebar.Item
				small
				icon={<SuitcaseIcon />}
				title={t('settings.jobs.title')}
				path="/settings/jobs"
			/>
			{hasCycles && (
				<Sidebar.Item
					small
					icon={<HistoryIcon />}
					title={t('settings.cycles.title')}
					path="/settings/cycles"
				/>
			)}
			{!isHRBusinessPartner && (
				<Sidebar.Item
					small
					icon={<BellIcon />}
					title={t('settings.notifications.title')}
					path="/settings/notifications"
				/>
			)}
			{hasBilling && (
				<Sidebar.Item
					small
					icon={<CashIcon />}
					title={t('settings.billing.title')}
					path="/settings/billing"
				/>
			)}
			<Sidebar.SectionTitle mt={4} mb={1}>
				{t('settings.section.content')}
			</Sidebar.SectionTitle>
			<Sidebar.Item
				small
				icon={<HammerIcon />}
				title={t('settings.competencies.title')}
				path="/settings/competencies"
			/>
			{hasGoalTemplates && (
				<Sidebar.Item
					small
					icon={<TargetIcon />}
					title={t('settings.goals.title')}
					path="/settings/goals"
				/>
			)}
			{hasFeedback && (
				<Sidebar.Item
					small
					icon={<DialogueIcon />}
					title={t('settings.feedback.title')}
					path="/settings/feedback"
				/>
			)}
			{hasConversations && (
				<Sidebar.Item
					small
					icon={<ConversationIcon />}
					title={t('settings.conversations.title')}
					path="/settings/conversations"
				/>
			)}
			{hasTalentReviews && (
				<Sidebar.Item
					small
					icon={<GridIcon />}
					title={t('settings.talentReviews.title')}
					path="/settings/talent-reviews"
				/>
			)}
			{hasLearning && (
				<Sidebar.Item
					small
					icon={<LibraryIcon />}
					title={t('settings.learning.title')}
					path="/settings/learning"
				/>
			)}
			{hasEngagement && (
				<Sidebar.Item
					small
					icon={<HeartRateIcon />}
					title={t('settings.engagement.title')}
					path="/settings/engagement"
				/>
			)}
			<Sidebar.SectionTitle mt={4} mb={1}>
				{t('settings.section.advanced')}
			</Sidebar.SectionTitle>
			<Sidebar.Item
				small
				icon={<SyncIcon />}
				title={t('settings.connectors.title')}
				path="/settings/integrations"
			/>
			<Sidebar.Item
				small
				icon={<LockIcon />}
				title={t('settings.roles.title')}
				path="/settings/roles"
			/>
			<Sidebar.Item
				small
				icon={<SuitcaseIcon />}
				title={t('settings.jobPositions.title')}
				path="/settings/job-positions"
			/>
			<Sidebar.Item
				small
				icon={<GearIcon />}
				title={t('settings.advanced.title')}
				path="/settings/advanced"
			/>
			{workspace.id === 'launch' && (
				<Sidebar.Item
					small
					icon={<GridIcon />}
					title={t('settings.workspaces.title')}
					path="/settings/workspaces"
				/>
			)}
		</>
	);
};
